import { Grid, Typography, FormControlLabel, Checkbox, Select, MenuItem, RadioGroup, Radio } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useState, useEffect, useCallback } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getFormSettingFields } from 'src/shared/services/memberFormSettingsService';
import { getAllProfileGroups } from 'src/shared/services/profileGroupService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';

// Statiska (icke-profil) export-kolumner. Profilkolumnerna läggs till dynamiskt – en per
// profilgrupp i medlemsregistret – under "profileGroup_{memberProfile_id}".
export const exportProperties = [
  'createDate',
  'memberNr',
  'sex',
  'firstName',
  'lastName',
  'birthDate',
  'COAddress',
  'address',
  'postNr',
  'postAddress',
  'invoiceAddress',
  'email',
  'phone',
  'phoneOther',
  'newspaper',
  'localAss',
  'district',
  'country',
  'countyName',
  'municipalityName',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
  'admDate',
  'expireDate',
  'resDate',
  'age',
] as const;

// Prefix för en dynamisk profilkolumn: "profileGroup_{memberProfile_id}".
export const PROFILE_GROUP_PREFIX = 'profileGroup_';

// Define a type for the form fields based on the exportProperties
type FormFields = {
  [key in (typeof exportProperties)[number]]: boolean;
} & {
  sortField: (typeof exportProperties)[number]; // Add sortOrder as a separate property
  orderDirection: 'asc' | 'desc'; // Add orderDirection as a separate property
  familyHeadMemberNr: boolean;
};

type ProfileColumn = { key: string; label: string };

type MemberAdvancedSearchExportFilterProps = {
  updateState: (state: Map<string, string | number | boolean>) => void;
};

export const MemberAdvancedSearchExportFilter = (props: MemberAdvancedSearchExportFilterProps) => {
  const { updateState } = props;
  const { t } = useTranslation(['member']);
  const { currentUser } = useGlobals();

  const { handleSubmit, control, setValue, getValues } = useForm<FormFields>({
    defaultValues: {
      ...exportProperties.reduce((acc, prop) => {
        acc[prop] = true;
        return acc;
      }, {} as FormFields),
      sortField: 'memberNr', // Default sort field
      orderDirection: 'asc', // Default sort order
      familyHeadMemberNr: false,
    },
  });

  // Form submission handler
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = (data: FormFields) => {
    return true;
  };

  // Custom-fältens rubriker (custom{N} -> heading) samt vilka custom-fält som faktiskt
  // används i medlemsformuläret (Show=true). Oanvända custom-fält ska inte visas/exporteras.
  const [customLabels, setCustomLabels] = useState<Record<string, string>>({});
  const [activeCustomFields, setActiveCustomFields] = useState<Set<string> | null>(null);

  useEffect(() => {
    let active = true;
    getFormSettingFields('member')
      .then((res) => {
        if (!active) return;
        const map: Record<string, string> = {};
        const used = new Set<string>();
        (res.data ?? []).forEach((f) => {
          if (!f?.fieldName) return;
          const m = f.fieldName.trim().match(/^custom(\d+)$/i);
          if (!m) return;
          const n = parseInt(m[1], 10);
          if (n < 1 || n > 10) return;
          if (f.show) used.add(`custom${n}`);
          if (f.show && f.heading?.trim()) map[`custom${n}`] = f.heading.trim();
        });
        setCustomLabels(map);
        setActiveCustomFields(used);
        // Avmarkera oanvända custom-fält så de inte hamnar i exporten.
        for (let n = 1; n <= 10; n += 1) {
          if (!used.has(`custom${n}`)) setValue(`custom${n}` as keyof FormFields, false);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [setValue]);

  // Custom-kolumn (custom1..10)?
  const isCustomProp = (prop: string) => /^custom\d+$/.test(prop);
  // Synlig kolumn: alla utom oanvända custom-fält (medan formulärinställningar laddas visas alla).
  const isVisibleProp = (prop: string) => !isCustomProp(prop) || activeCustomFields === null || activeCustomFields.has(prop);
  const visibleProps = exportProperties.filter(isVisibleProp);

  // En kolumn per profilgrupp i medlemsregistret, döpt "Profil: {titel}". Värdet i exporten
  // blir medlemmens valda värden för den profilen.
  const [profileColumns, setProfileColumns] = useState<ProfileColumn[]>([]);
  const [profileSelection, setProfileSelection] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;
    getAllProfileGroups(false, true)
      .then((res) => {
        if (!active) return;
        const groups = res.data?.results ?? [];
        const cols: ProfileColumn[] = groups
          .filter((g) => g?.memberProfile_id && g.title)
          .map((g) => ({ key: `${PROFILE_GROUP_PREFIX}${g.memberProfile_id}`, label: `Profil: ${g.title}` }));
        setProfileColumns(cols);
        setProfileSelection(
          cols.reduce((acc, c) => {
            acc[c.key] = true;
            return acc;
          }, {} as Record<string, boolean>),
        );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // Rubrik för en statisk kolumn:
  //  - Förening/Distrikt: kundens konfigurerade etikett (inställningar local/county-label).
  //  - Custom-fält: medlemsformulärets rubrik.
  //  - Övrigt: standardtexten (i18n).
  const getColumnLabel = useCallback(
    (prop: string) => {
      if (prop === 'localAss')
        return getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:exportModal.formField.localAss'));
      if (prop === 'district')
        return getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:exportModal.formField.district'));
      return customLabels[prop] ?? t(`member:exportModal.formField.${prop}`);
    },
    [customLabels, currentUser, t],
  );

  // State for master checkbox
  const [allChecked, setAllChecked] = useState(true);
  // Watch form values
  const values = useWatch({ control });

  // Method to serialize state
  const serializeState = useCallback(() => {
    // Serialize state into an array of entries, filtering out falsy values
    const serializedState: [string, string | number | boolean][] = Object.entries(getValues()).filter(([, value]) => !!value) as [
      string,
      string | number | boolean,
    ][];
    // Lägg till valda profilkolumner (profileGroup_{id}).
    const profileEntries = Object.entries(profileSelection).filter(([, value]) => !!value) as [string, boolean][];
    updateState(new Map([...serializedState, ...profileEntries]));
  }, [getValues, updateState, profileSelection]);

  // Serialize the state when any form value (or profilval) is changed
  useEffect(() => {
    serializeState();
  }, [serializeState, values, profileSelection]);

  // Toggle all checkboxes (inkl. profilkolumner) when master checkbox changes
  const handleMasterCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setAllChecked(isChecked);
    visibleProps.forEach((prop) => setValue(prop, isChecked));
    setProfileSelection((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = isChecked;
        return acc;
      }, {} as Record<string, boolean>),
    );
  };

  const toggleProfile = (key: string, checked: boolean) => {
    setProfileSelection((prev) => ({ ...prev, [key]: checked }));
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ p: 3, mt: -2 }}>
      <Grid container>
        <Grid item xs={12} sx={{ mb: 1 }}>
          <Typography variant='h6'>{t('member:exportModal.selectColumnTitle')}</Typography>
          <Typography variant='body2'>{t('member:exportModal.selectColumnDescription')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            sx={{ '& > .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 600 } }}
            control={<Checkbox checked={allChecked} onChange={handleMasterCheckboxChange} />}
            label={t('member:exportModal.checkAll')}
          />
        </Grid>
        {visibleProps.map((prop) => (
          <Grid item xs={6} md={4} lg={3} key={prop}>
            <Controller
              key={prop}
              name={prop}
              control={control}
              render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={getColumnLabel(prop)} />}
            />
          </Grid>
        ))}
        {profileColumns.map((col) => (
          <Grid item xs={6} md={4} lg={3} key={col.key}>
            <FormControlLabel
              control={<Checkbox checked={profileSelection[col.key] ?? false} onChange={(e) => toggleProfile(col.key, e.target.checked)} />}
              label={col.label}
            />
          </Grid>
        ))}
        <Grid item xs={12} sx={{ mt: 2 }} />
        <Grid item xs={12} md={8} lg={9}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {t('member:exportModal.sortHeader')}
          </Typography>
          <Stack direction='row' spacing={2}>
            <Controller
              name='sortField'
              control={control}
              render={({ field }) => (
                <Select {...field} size='small' fullWidth>
                  {visibleProps.map((property) => (
                    <MenuItem key={property} value={property}>
                      {getColumnLabel(property)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name='orderDirection'
              control={control}
              render={({ field }) => (
                <Box sx={{ minWidth: '15rem' }}>
                  <RadioGroup row {...field} value={field.value}>
                    <FormControlLabel value='asc' control={<Radio />} label={t('member:exportModal.sort.asc')} />
                    <FormControlLabel value='desc' control={<Radio />} label={t('member:exportModal.sort.desc')} />
                  </RadioGroup>
                </Box>
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {t('member:exportModal.familyHeader')}
          </Typography>
          <Controller
            name='familyHeadMemberNr'
            control={control}
            render={({ field }) => (
              <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={getColumnLabel('familyHeadMemberNr')} />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
